import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useMemo, useEffect, useImperativeHandle, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classnames from 'classnames';
import { debounce } from '@hh.ru/magritte-common-func-utils';
import { Cell, CellText } from '@hh.ru/magritte-ui-cell';
import { CheckableCardElement } from '@hh.ru/magritte-ui-checkable-card/CheckableCardElement';
import { Action } from './Action.js';
import { MobileDelimiter } from './MobileDelimiter.js';
import { MobileItem } from './MobileItem.js';
import { MobileParentItem } from './MobileParentItem.js';
import { s as styles } from './tree-selector-item-B-I_mxX1.js';
import '@hh.ru/magritte-ui-checkbox-radio';
import '@hh.ru/magritte-ui-card';
import '@hh.ru/magritte-ui-typography';
import './TreeSelectorItemBase.js';
import '@hh.ru/magritte-ui-badge';

const DEFAULT_ITEM_HEIGHT = 56;
const SCROLLEND_HANDLER_DEBOUNCE_MS = 150;
const MobileItemsListComponent = (props, ref) => {
    const { collection, selected, disabled, leavesOnly, singleChoice, onChange, checkSelectable, getMobileSearchItemOrder, setInputValue, handleChangeInput, isSearch, renderMobileDelimiter, renderItem, contentFilterQuery, getSelectAllParentTrl, renderContentBefore, onMobileNavigationChange, } = props;
    const navigationChangeCallback = useRef(onMobileNavigationChange);
    navigationChangeCallback.current = onMobileNavigationChange;
    const prevParentIds = useRef([]);
    const prevQueries = useRef([]);
    const currentQuery = useRef(contentFilterQuery);
    const [currentParentId, setCurrentParentId] = useState();
    const items = useMemo(() => (currentParentId ? collection.getChildren(currentParentId) : collection.getTopLevel()), [currentParentId, collection]);
    const orderHashMap = useMemo(() => {
        return items.reduce((acc, item) => {
            acc[item.id] = isSearch && !currentParentId ? getMobileSearchItemOrder(item) : 0;
            return acc;
        }, {});
    }, [items, isSearch, currentParentId, getMobileSearchItemOrder]);
    const sortedItems = useMemo(() => {
        return items.sort((itemA, itemB) => {
            return orderHashMap[itemA.id] - orderHashMap[itemB.id];
        });
    }, [items, orderHashMap]);
    const isSelectable = currentParentId && !leavesOnly && checkSelectable?.(currentParentId, collection);
    const withContentBefore = !!renderContentBefore;
    const count = items.length + (isSelectable ? 1 : 0) + (withContentBefore ? 1 : 0);
    const parentRef = useRef(null);
    const isScrolling = useRef(false);
    const getItemKey = (index) => {
        if (withContentBefore && index === 0) {
            return 'promo';
        }
        const isSelectAllItem = isSelectable && ((index === 0 && !withContentBefore) || (index === 1 && withContentBefore));
        if (isSelectAllItem) {
            return `current${currentParentId}`;
        }
        const itemIndex = index - (isSelectable ? 1 : 0) - (withContentBefore ? 1 : 0);
        const item = sortedItems[itemIndex];
        return currentParentId ? `${currentParentId}${item.id}` : `${item.id}`;
    };
    const virtualizer = useVirtualizer({
        count,
        getScrollElement: () => parentRef.current,
        estimateSize: () => DEFAULT_ITEM_HEIGHT,
        getItemKey,
    });
    const itemsVirtualizer = virtualizer.getVirtualItems();
    useEffect(() => {
        setCurrentParentId(undefined);
        prevParentIds.current = [];
        prevQueries.current = [];
        navigationChangeCallback.current?.(undefined);
        currentQuery.current = contentFilterQuery;
    }, [contentFilterQuery]);
    useImperativeHandle(ref, () => ({
        back: () => {
            if (!isScrolling.current) {
                const prevParentId = prevParentIds.current.pop();
                setCurrentParentId(prevParentId);
                navigationChangeCallback.current?.(prevParentId);
                if (prevQueries.current.length) {
                    const prevInputValue = prevQueries.current.pop();
                    setInputValue(prevInputValue);
                    currentQuery.current = prevInputValue;
                }
                else {
                    handleChangeInput('');
                }
            }
        },
    }), [handleChangeInput, setInputValue]);
    useEffect(() => {
        const handleScrollEnd = debounce(() => {
            isScrolling.current = false;
        }, SCROLLEND_HANDLER_DEBOUNCE_MS);
        const handleScroll = () => {
            isScrolling.current = true;
            handleScrollEnd();
        };
        parentRef.current?.addEventListener('scroll', handleScroll);
        return () => {
            parentRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, []);
    const refCallback = useCallback((node) => {
        parentRef.current = node ? node.parentElement : null;
    }, []);
    useEffect(() => {
        virtualizer.scrollToOffset(0);
    }, [currentParentId, sortedItems, virtualizer]);
    return (jsx("div", { ref: refCallback, className: styles.virtualizedList, style: {
            height: `${virtualizer.getTotalSize()}px`,
        }, children: itemsVirtualizer.map((virtualItem) => {
            const isContentBeforeItem = withContentBefore && virtualItem.index === 0;
            const isSelectAllItem = isSelectable &&
                ((virtualItem.index === 0 && !withContentBefore) || (virtualItem.index === 1 && withContentBefore));
            if (isContentBeforeItem) {
                return (jsx("div", { ref: virtualizer.measureElement, "data-index": virtualItem.index, className: styles.virtualizedItem, style: {
                        transform: `translateY(${virtualItem.start}px)`,
                    }, children: renderContentBefore(isSearch) }, virtualItem.key));
            }
            if (isSelectAllItem) {
                return (jsx("div", { ref: virtualizer.measureElement, "data-index": virtualItem.index, className: styles.virtualizedItem, style: {
                        transform: `translateY(${virtualItem.start}px)`,
                    }, children: jsx("div", { className: styles.mobileItem, children: jsx(CheckableCardElement, { padding: 16, flexible: true, borderRadius: 12, Element: "label", checked: selected.includes(currentParentId), "data-qa": `tree-selector-select-all-${currentParentId}`, children: jsx(Cell, { Element: 'div', disabled: disabled.includes(currentParentId), right: jsx(Action, { selected: selected.includes(currentParentId), onChange: onChange, id: currentParentId, singleChoice: singleChoice }), children: jsx(CellText, { children: getSelectAllParentTrl(currentParentId) }) }) }) }) }, virtualItem.key));
            }
            const itemIndex = virtualItem.index - (isSelectable ? 1 : 0) - (withContentBefore ? 1 : 0);
            const item = sortedItems[itemIndex];
            const hasChildren = collection.hasChildren(item.id);
            const hasLetter = item.additional?.char && !isSearch;
            const delimiterContent = renderMobileDelimiter?.({
                item,
                order: orderHashMap[item.id],
                isSearch,
                index: itemIndex,
                isTopLevel: !currentParentId,
            });
            const withGap = (!!withContentBefore && virtualItem.index !== 1) || (!withContentBefore && virtualItem.index !== 0);
            return (jsx("div", { ref: virtualizer.measureElement, "data-index": virtualItem.index, style: {
                    transform: `translateY(${virtualItem.start}px)`,
                }, className: classnames(styles.virtualizedItem, {
                    [styles.withGap]: withGap,
                }), children: jsxs("div", { className: styles.mobileItem, children: [hasLetter && jsx(MobileDelimiter, { children: item.additional?.char }), delimiterContent && jsx(MobileDelimiter, { children: delimiterContent }), hasChildren ? (jsx(MobileParentItem, { collection: collection, item: item, selected: selected, onClick: () => {
                                prevParentIds.current.push(currentParentId);
                                prevQueries.current.push(currentQuery.current);
                                setCurrentParentId(item.id);
                                setInputValue('');
                                currentQuery.current = '';
                                navigationChangeCallback.current?.(item.id);
                            }, parentId: currentParentId, isSearch: isSearch, renderItem: renderItem })) : (jsx(MobileItem, { item: item, singleChoice: singleChoice, isSelectable: checkSelectable?.(item.id, collection), isSelected: selected.includes(item.id), isDisabled: disabled.includes(item.id), onChange: onChange, isSearch: isSearch, renderItem: renderItem, parentId: currentParentId }))] }) }, virtualItem.key));
        }) }));
};
const MobileItemsList = forwardRef(MobileItemsListComponent);

export { MobileItemsList };
//# sourceMappingURL=MobileItemsList.js.map
